/**
 * Circular progress ring showing days until a quote expires.
 */
import React from 'react';
import type { QuoteExpiryRingProps } from './QuoteExpiryRing.types';

const RING_SIZE = 36;
const STROKE = 3;
const CENTER = RING_SIZE / 2;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Visual fill assumes a ~30-day quote window. */
const FULL_WINDOW_DAYS = 30;

export type { QuoteExpiryRingProps } from './QuoteExpiryRing.types';

/** Whole days from today until `expiryStr`, or a `(N Days)` suffix already in the string. */
export function daysUntilQuoteExpiry(expiryStr: string | null | undefined): number | null {
    if (expiryStr == null || String(expiryStr).trim() === '') return null;
    const text = String(expiryStr);
    const parenDays = text.match(/\((\d+)\s*Days?\)/i);
    if (parenDays) {
        const n = Number(parenDays[1]);
        return Number.isFinite(n) ? n : null;
    }
    const parsed = Date.parse(text);
    if (Number.isNaN(parsed)) return null;
    const end = new Date(parsed);
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((end.getTime() - today.getTime()) / 86400000));
}

export function QuoteExpiryRing({ days }: QuoteExpiryRingProps) {
    const safeDays = days == null || !Number.isFinite(days) ? null : Math.max(0, Math.round(days));
    const progress = safeDays == null ? 0 : Math.min(1, safeDays / FULL_WINDOW_DAYS);
    const dash = CIRCUMFERENCE * progress;
    const label = safeDays == null ? 'Expiry unknown' : `${safeDays} day${safeDays === 1 ? '' : 's'} remaining`;

    return (
        <div className="obd-quote-expiry-ring" title={label} aria-label={label}>
            <svg
                className="obd-quote-expiry-ring__svg"
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                aria-hidden
            >
                <circle
                    className="obd-quote-expiry-ring__track"
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill="none"
                    strokeWidth={STROKE}
                />
                <circle
                    className="obd-quote-expiry-ring__progress"
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill="none"
                    strokeWidth={STROKE}
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
                    transform={`rotate(-90 ${CENTER} ${CENTER})`}
                />
            </svg>
            <span className="obd-quote-expiry-ring__value">
                {safeDays == null ? '—' : safeDays}
                {safeDays != null ? <span className="obd-quote-expiry-ring__unit">d</span> : null}
            </span>
        </div>
    );
}
